import apiClient from '../client';

// Payroll: upload the week's FedEx statement, see what to pay each
// person. The server does the reading and the arithmetic; this file is
// the shapes and the calls.
//
// NOTE: the shared apiClient's response interceptor returns response.data,
// so every call here resolves to the response BODY.

export type PayRuleKind =
  | 'day_rate' | 'per_stop' | 'over_threshold' | 'hourly' | 'weekly_salary'
  | 'training_day' | 'vacation_day' | 'bonus' | 'deduction';
export type PayRulePeriod = 'day' | 'week' | 'pooled';
export type PayBonusMetric = 'days' | 'stops' | 'packages' | 'stops_per_day';
export type StopKind = 'pickup' | 'delivery' | 'ecommerce';
export type PayrollRunStatus = 'draft' | 'finalized';
export type PayrollLineStatus = 'ok' | 'no_rules' | 'absent' | 'skipped';
export type AdjustmentKind = 'bonus' | 'deduction' | 'vacation' | 'correction';

export interface PayRule {
  id: string;
  kind: PayRuleKind;
  amount: number | null;
  threshold?: number | null;
  period?: PayRulePeriod;
  metric?: PayBonusMetric;
  label?: string;
  enabled?: boolean;
}

export interface Person {
  accountId: string;
  name: string;
  email: string;
  role: string;
  status: string;
  profileId: string | null;
  fedexId: string;
  rules: PayRule[];
  stopKinds: StopKind[];
  daysThatCount: number[];
  notes: string;
  /** "$200 a day · $1.25 over 160 a day", from the server so every screen agrees. */
  description: string;
  /** A rule is switched on but has no amount yet. */
  incomplete: boolean;
  /** At least one rule will actually produce a number. */
  hasRules: boolean;
  updatedAt: string | null;
  refreshedDrafts?: number;
  /** Finalized weeks this person was on with no usable rules, so paid nothing. */
  unpaidFinalizedWeeks?: number;
  filledFinalized?: number;
}

/** A setup to start somebody from. Copied when picked, never linked. */
export interface PaySetup {
  rules: PayRule[];
  stopKinds: StopKind[] | null;
  daysThatCount: number[];
  description: string;
}
/** One change to many people's rules. */
export type BulkOp =
  | { type: 'template'; rules: PayRule[]; stopKinds?: StopKind[] | null; daysThatCount?: number[] }
  | { type: 'set_amount'; kind: PayRuleKind; amount: number }
  | { type: 'raise_amount'; kind: PayRuleKind; by: number }
  | { type: 'add_rule'; rule: Partial<PayRule> & { kind: PayRuleKind } }
  | { type: 'remove_kind'; kind: PayRuleKind };
export interface BulkPreviewRow {
  accountId: string; name: string; status: string; before: string; after: string; changed: boolean; unpaidFinalizedWeeks: number;
}
export interface PayTemplate extends PaySetup { id: string; name: string; updatedAt: string | null }
export interface CommonSetup extends PaySetup { people: number }

export interface ProfileInput {
  /** Also give these rules to the finalized weeks this person was paid nothing in. */
  fillFinalized?: boolean;
  rules?: PayRule[];
  fedexId?: string;
  stopKinds?: StopKind[];
  daysThatCount?: number[];
  notes?: string;
}

export interface LineDay {
  date: string;
  pickupPackages: number;
  pickupStops: number;
  deliveryPackages: number;
  deliveryStops: number;
  ecommercePackages: number;
  ecommerceStops: number;
  stops: number;
  packages: number;
  /** Driven by this person under somebody else's FedEx ID: the row it is on in the statement. */
  movedFrom?: { lineId: string; fedexId: string; name: string } | null;
  /** Somebody else's scans added to this day: a trainee's stops given to their trainer. */
  mergedFrom?: { lineId: string; fedexId: string; name: string; reason: string; stops: number }[];
}

export interface ReceiptLine {
  ruleId: string;
  kind: PayRuleKind;
  label: string;
  detail: string;
  amount: number;
  incomplete: boolean;
  /** Taken off this one week; the person's rules still have it. */
  skipped?: boolean;
}

export interface Adjustment {
  _id: string;
  kind: AdjustmentKind;
  amount: number;
  note: string;
  byName: string;
  at: string;
}

export interface LineTotals {
  days: number;
  stops: number;
  packages: number;
  pickupStops: number;
  deliveryStops: number;
  ecommerceStops: number;
  /** The statement's own counts, kept when a number was typed over them. */
  statementDays?: number;
  statementStops?: number;
  statementPackages?: number;
  daysTyped?: boolean;
  stopsTyped?: boolean;
  packagesTyped?: boolean;
  /** Days in training (paid as training days) and what was scanned on them. */
  trainingDays?: number;
  trainingStops?: number;
}

export interface LineInputs {
  /** Typed over the statement's counts; null means "as scanned". */
  daysWorked: number | null;
  stops: number | null;
  packages: number | null;
  hours: number | null;
  trainingDays: number;
  vacationDays: number;
  /** Rules not paid this week only. */
  skipRuleIds?: string[];
}

export interface PayrollLine {
  _id: string;
  runId: string;
  accountId: string | null;
  profileId: string | null;
  /** Added by hand for somebody outside the company list. */
  custom?: boolean;
  fedexId: string;
  statementName: string;
  name: string;
  status: PayrollLineStatus;
  days: LineDay[];
  /** Days of the week they were in training and out working (YYYY-MM-DD), from their training period. */
  trainingDates?: string[];
  totals: LineTotals;
  inputs: LineInputs;
  rulesSnapshot: PayRule[];
  receipt: ReceiptLine[];
  calculated: number;
  adjustments: Adjustment[];
  adjustmentsTotal: number;
  total: number;
  /** This week only. */
  note: string;
  /** The person's own note, shown on every week (lives on the pay profile). */
  standingNote?: string;
}

export interface RunTotals {
  calculated: number;
  adjustments: number;
  gross: number;
  people: number;
  paid: number;
  noRules: number;
  dispatches: number;
  stops: number;
  packages: number;
}

export interface StatementDay {
  date: string;
  pickupPackages: number;
  pickupStops: number;
  deliveryPackages: number;
  deliveryStops: number;
  ecommercePackages: number;
  ecommerceStops: number;
  totalStops: number;
  fuelAmount: number;
  totalAmount: number;
}

export interface PayrollRun {
  _id: string;
  weekStart: string;
  weekEnd: string;
  status: PayrollRunStatus;
  source: { fileName: string; serviceArea: string; facility: string; uploadedAt: string; uploadedBy?: string };
  /** The week's files as filed in Files, once they have been. */
  files?: { statementDocumentId?: string | null; weeklyServiceDocumentId?: string | null };
  statement: {
    days?: StatementDay[];
    totalStops: number;
    totalPackages: number;
    pickupDeliveryTotal: number;
    otherChargesTotal: number;
    /** Before FedEx's deductions: what the week earned. The ratio is measured against this. */
    totalCharges: number;
    /** FedEx's chargebacks and deductions, negative. totalCharges + chargebacks = netAmountDue. */
    chargebacks?: number;
    netAmountDue: number;
    otherCharges?: { type: string; description: string; amount: number }[];
    deductions?: { type: string; description: string; amount: number }[];
    driverStopsTotal: number;
    reconciled: boolean;
  };
  totals: RunTotals;
  ratio: number | null;
  finalizedAt: string | null;
  /** What the week's review found and did: training days, trainee stops, shared routes. */
  review?: WeekReview;
  createdAt?: string;
}

export interface WeekReviewItem {
  key: string;
  kind: string;
  /** done: a change it made; warn: needs a look; info: worth knowing. */
  tone: 'done' | 'info' | 'warn';
  text: string;
  accountIds: string[];
  date: string | null;
  workArea: string | null;
  undoable: boolean;
  undone: boolean;
}

export interface WeekReview {
  at: string | null;
  worksheetWeekEnd: string | null;
  items: WeekReviewItem[];
  dismissed: string[];
}

export interface RunDetail {
  run: PayrollRun;
  lines: PayrollLine[];
  previous: { runId: string; weekEnd: string; ratio: number | null; gross: number } | null;
  next: { runId: string; weekEnd: string } | null;
}

export interface StatementPreview {
  week: {
    weekStart: string;
    weekEnd: string;
    serviceArea: string;
    facility: string;
    totals: { totalStops: number; totalPackages: number; netAmountDue: number; totalCharges: number; pickupDeliveryTotal: number; otherChargesTotal: number };
    reconciliation: { driverStops: number; statementStops: number; ok: boolean };
  };
  existing: { runId: string; status: PayrollRunStatus; uploadedAt: string } | null;
  drivers: {
    fedexId: string;
    name: string;
    totals: LineTotals;
    match: { accountId: string; name: string; how: 'profile' | 'candidate' } | null;
    suggestions: { accountId: string; name: string; likely: boolean }[];
  }[];
  absent: { accountId: string; name: string; salaried: boolean; description: string }[];
}

export interface LineUpdate {
  inputs?: Partial<LineInputs>;
  note?: string;
  standingNote?: string;
  addAdjustment?: { kind: AdjustmentKind; amount: number; note?: string };
  removeAdjustmentId?: string;
  /** Leave a calculated line off this week, or put it back. */
  skipRule?: string;
  restoreRule?: string;
  accountId?: string;
}

export interface RulePreview {
  weekEnd: string | null;
  paid: number | null;
  receipt: ReceiptLine[];
  calculated: number;
  totals: LineTotals;
  weeks: string[];
}

export type EmployerTaxSource = 'actual' | 'estimate';

/**
 * The money side of a set of weeks, worked out the same way for one week
 * and for the total. Revenue is the statement's total before FedEx's
 * deductions, which come off `margin` only; labour
 * cost is payroll plus employer tax; stops and packages are the
 * statement's own counts, on both sides of "per stop".
 */
export interface ReportEconomics {
  laborCost: number;
  margin: number;
  ratio: number | null;
  laborRatio: number | null;
  costPerStop: number | null;
  revenuePerStop: number | null;
  costPerPackage: number | null;
  revenuePerPackage: number | null;
  costPerDispatch: number | null;
  revenuePerDispatch: number | null;
}

export interface ReportWeek extends ReportEconomics {
  runId: string;
  weekStart: string;
  weekEnd: string;
  status: PayrollRunStatus;
  gross: number;
  calculated: number;
  adjustments: number;
  netAmountDue: number;
  totalCharges: number;
  /** Before deductions; what the ratios are measured against. */
  revenue: number;
  /** FedEx's chargebacks and deductions, negative. */
  deductions: number;
  /** Typed in from the payroll provider, else estimated from the company rate, else null. */
  employerTax: number | null;
  employerTaxSource: EmployerTaxSource | null;
  dispatches: number;
  stops: number;
  packages: number;
  people: number;
}

export interface ReportSummary extends ReportEconomics {
  weeks: number;
  gross: number;
  netAmountDue: number;
  revenue: number;
  deductions: number;
  employerTax: number | null;
  employerTaxEstimatedWeeks: number;
  employerTaxMissingWeeks: number;
  averageGross: number;
  stops: number;
  packages: number;
  dispatches: number;
}

/** Either the last N weeks, or every week touching from..to (YYYY-MM-DD). */
export type ReportQuery = { weeks: number } | { from?: string; to?: string };

export interface ReportPerson {
  accountId: string;
  name: string;
  weeks: number;
  days: number;
  stops: number;
  packages: number;
  pay: number;
  stopsPerDay: number | null;
  payPerStop: number | null;
  payPerDay: number | null;
  series: { weekEnd: string; stops: number; days: number; packages: number; pay: number }[];
}

export interface Reports {
  weeks: ReportWeek[];
  people: ReportPerson[];
  summary: ReportSummary | null;
  employerTaxRate: number | null;
  range: { from: string | null; to: string | null; truncated: boolean };
}

export interface PayrollSettings {
  defaultRules: PayRule[];
  isProductDefault: boolean;
  employerTaxRate: number | null;
}

export interface PersonWeek {
  _id: string;
  runId: string;
  weekEnd: string;
  weekStart: string | null;
  status: PayrollLineStatus;
  runStatus: PayrollRunStatus;
  totals: LineTotals;
  inputs: LineInputs;
  calculated: number;
  adjustmentsTotal: number;
  total: number;
  receipt: ReceiptLine[];
}

export function describeError(err: any, fallback: string): string {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

export interface VolumeWeek {
  weekEnding: string;
  pickupPackages: number;
  pickupStops: number;
  deliveryPackages: number;
  deliveryStops: number;
  ecommercePackages: number;
  ecommerceStops: number;
  totalPackages: number;
  totalStops: number;
  revenue: number | null;
  source: 'statement' | 'history';
  runId?: string;
}

export interface VolumeImportSummary {
  weeks: number;
  byYear: Record<string, number>;
  first: string;
  last: string;
  corrections: { from: string; to: string; year: number }[];
  overlaps: number;
  agreeing: number;
  disagreements: {
    weekEnding: string;
    differences: { field: 'totalPackages' | 'totalStops' | 'revenue'; sheet: number; statement: number }[];
  }[];
  written?: number;
}

export default class PayrollService {
  private static readonly BASE = '/api/payroll';

  static async people(): Promise<Person[]> {
    const data: any = await apiClient.get(`${this.BASE}/people`);
    return data.people;
  }

  static async person(accountId: string): Promise<Person> {
    const data: any = await apiClient.get(`${this.BASE}/people/${accountId}`);
    return data.person;
  }

  /**
   * Somebody on a statement who is not in the company yet. Former: no email
   * needed, no login, no invitation. Current: invited when an email and
   * phone are given, otherwise created as not yet invited.
   */
  static async addPerson(input: {
    firstName: string; lastName: string; status: 'former' | 'current'; fedexId?: string;
    email?: string; phone?: string; rules?: PayRule[]; stopKinds?: StopKind[] | null; daysThatCount?: number[];
  }): Promise<Person> {
    const data: any = await apiClient.post(`${this.BASE}/people`, input);
    return data.person;
  }

  /** What one change would do to each of these people's rules. Writes nothing. */
  static async bulkPreview(accountIds: string[], op: BulkOp): Promise<{ people: BulkPreviewRow[] }> {
    return apiClient.post(`${this.BASE}/people/bulk/preview`, { accountIds, op }) as any;
  }

  static async bulkApply(accountIds: string[], op: BulkOp, fillFinalized: boolean): Promise<{ changed: number; unchanged: number; refreshedDrafts: number; filledFinalized: number }> {
    return apiClient.post(`${this.BASE}/people/bulk`, { accountIds, op, fillFinalized }) as any;
  }

  static async templates(): Promise<{ saved: PayTemplate[]; common: CommonSetup[] }> {
    return apiClient.get(`${this.BASE}/templates`) as any;
  }

  static async saveTemplate(input: { name: string; rules: PayRule[]; stopKinds?: StopKind[]; daysThatCount?: number[] }, id?: string): Promise<PayTemplate> {
    const data: any = id ? await apiClient.put(`${this.BASE}/templates/${id}`, input) : await apiClient.post(`${this.BASE}/templates`, input);
    return data.template;
  }

  static async deleteTemplate(id: string): Promise<void> {
    await apiClient.delete(`${this.BASE}/templates/${id}`);
  }

  static async saveProfile(accountId: string, input: ProfileInput): Promise<Person> {
    const data: any = await apiClient.put(`${this.BASE}/people/${accountId}`, input);
    return data.person;
  }

  static async previewRules(accountId: string, input: ProfileInput & { weekEnd?: string }): Promise<RulePreview> {
    return apiClient.post(`${this.BASE}/people/${accountId}/preview`, input) as any;
  }

  static async personHistory(accountId: string, weeks = 26): Promise<PersonWeek[]> {
    const data: any = await apiClient.get(`${this.BASE}/people/${accountId}/history?weeks=${weeks}`);
    return data.weeks;
  }

  static async settings(): Promise<PayrollSettings> {
    return apiClient.get(`${this.BASE}/settings`) as any;
  }

  static async saveSettings(defaultRules: PayRule[]): Promise<PayrollSettings> {
    return apiClient.put(`${this.BASE}/settings`, { defaultRules }) as any;
  }

  /** Percent of gross; null stops estimating. Leaves the default rules alone. */
  static async saveEmployerTaxRate(employerTaxRate: number | null): Promise<PayrollSettings> {
    return apiClient.put(`${this.BASE}/settings`, { employerTaxRate }) as any;
  }

  static async previewStatement(text: string, fileName: string): Promise<StatementPreview> {
    return apiClient.post(`${this.BASE}/statements/preview`, { text, fileName }) as any;
  }

  static async commitStatement(input: {
    text: string; fileName: string; matches: Record<string, string>; replaceFinalized?: boolean;
  }): Promise<RunDetail> {
    return apiClient.post(`${this.BASE}/statements`, input) as any;
  }

  /**
   * File the week's statement and, if there is one, its weekly service
   * report under Payroll in Files. Sent after the week is saved, so a
   * file that will not store never costs the week its pay.
   */
  /** Check the week again: training days, trainee stops, shared routes. */
  static async reviewRun(runId: string): Promise<RunDetail> {
    return apiClient.post(`${this.BASE}/runs/${runId}/review`, {}) as any;
  }

  /** Undo one change the review made (undo: false makes it again). */
  static async setReviewItem(runId: string, key: string, undo: boolean): Promise<RunDetail> {
    return apiClient.put(`${this.BASE}/runs/${runId}/review/items/${encodeURIComponent(key)}`, { undo }) as any;
  }

  static async attachFiles(runId: string, files: { statement?: File; weeklyService?: File | null }): Promise<{
    files: Partial<Record<'statement' | 'weeklyService', { documentId: string; fileName: string }>>;
  }> {
    const form = new FormData();
    if (files.statement) form.append('statement', files.statement);
    if (files.weeklyService) form.append('weeklyService', files.weeklyService);
    return apiClient.post(`${this.BASE}/runs/${runId}/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 5 * 60 * 1000,
    }) as any;
  }

  static async runs(): Promise<PayrollRun[]> {
    const data: any = await apiClient.get(`${this.BASE}/runs`);
    return data.runs;
  }

  static async run(id: string): Promise<RunDetail> {
    return apiClient.get(`${this.BASE}/runs/${id}`) as any;
  }

  static async updateLine(runId: string, lineId: string, input: LineUpdate): Promise<{ line: PayrollLine; totals: RunTotals }> {
    return apiClient.patch(`${this.BASE}/runs/${runId}/lines/${lineId}`, input) as any;
  }

  /** Give a statement day to the person who drove it (or send it back). */
  static async moveDay(runId: string, lineId: string, date: string, toLineId: string): Promise<{ lines: PayrollLine[]; totals: RunTotals }> {
    return apiClient.post(`${this.BASE}/runs/${runId}/lines/${lineId}/move-day`, { date, toLineId }) as any;
  }

  /** Somebody added to the week by hand: a member by account, or a typed name. */
  static async addLine(runId: string, input: { accountId?: string; name?: string }): Promise<{ line: PayrollLine; totals: RunTotals }> {
    return apiClient.post(`${this.BASE}/runs/${runId}/lines`, input) as any;
  }

  static async removeLine(runId: string, lineId: string): Promise<{ totals: RunTotals }> {
    return apiClient.delete(`${this.BASE}/runs/${runId}/lines/${lineId}`) as any;
  }

  static async finalize(id: string): Promise<RunDetail> {
    return apiClient.post(`${this.BASE}/runs/${id}/finalize`, {}) as any;
  }

  static async reopen(id: string, reason?: string): Promise<RunDetail> {
    return apiClient.post(`${this.BASE}/runs/${id}/reopen`, { reason }) as any;
  }

  static async remove(id: string): Promise<void> {
    await apiClient.delete(`${this.BASE}/runs/${id}`);
  }

  static async exportCsv(id: string): Promise<Blob> {
    const text: any = await apiClient.get(`${this.BASE}/runs/${id}/export`, { responseType: 'text' });
    return new Blob([typeof text === 'string' ? text : String(text)], { type: 'text/csv;charset=utf-8' });
  }

  static async sourceCsv(id: string): Promise<Blob> {
    const text: any = await apiClient.get(`${this.BASE}/runs/${id}/source`, { responseType: 'text' });
    return new Blob([typeof text === 'string' ? text : String(text)], { type: 'text/csv;charset=utf-8' });
  }

  // Weekly volume: every week the company has, statements first and the
  // imported history filling the gaps.
  static async volume(): Promise<VolumeWeek[]> {
    const body: { weeks: VolumeWeek[] } = await (apiClient.get(`${this.BASE}/volume`) as any);
    return body.weeks || [];
  }

  static async importVolume(text: string, dryRun: boolean): Promise<{ summary: VolumeImportSummary }> {
    return apiClient.post(`${this.BASE}/volume/import`, { text, dryRun }) as any;
  }

  static async reports(query: number | ReportQuery): Promise<Reports> {
    const q = typeof query === 'number' ? { weeks: query } : query;
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v !== undefined && v !== '') params.set(k, String(v)); });
    const r: Reports = await (apiClient.get(`${this.BASE}/reports?${params.toString()}`) as any);
    // A server from before revenue/deductions were split out sends neither.
    const fill = <T extends { revenue?: number; deductions?: number; totalCharges?: number; netAmountDue: number }>(w: T): T => ({
      ...w, revenue: w.revenue ?? (w.totalCharges || w.netAmountDue), deductions: w.deductions ?? 0,
    });
    return { ...r, weeks: (r.weeks || []).map(fill), summary: r.summary ? fill(r.summary) : r.summary };
  }

  /** The week's employer taxes as the payroll provider charged them; null goes back to the estimate. */
  static async setEmployerTax(runId: string, amount: number | null): Promise<{ runId: string; employerTax: { amount: number | null } }> {
    return apiClient.put(`${this.BASE}/runs/${runId}/employer-tax`, { amount }) as any;
  }
}
